import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Chip,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import type {
  PipelineEnvironment,
  PipelineMode,
  PipelineStageName,
  PipelineRunConfig,
  CreatePipelineRunRequest,
} from '../services/api-client';

interface NewRunDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CreatePipelineRunRequest) => void;
  loading: boolean;
}

const BATCH_PRESETS: { name: string; label: string; description: string; config: Partial<PipelineRunConfig> }[] = [
  {
    name: 'canary_5k',
    label: 'Canary 5K',
    description: 'Quick smoke test — 5,000 hotels, no images',
    config: { limit: '5000' },
  },
  {
    name: 'eu_only',
    label: 'EU Only',
    description: 'All EU countries only',
    config: { country_allowlist: 'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE' },
  },
  {
    name: 'hotel_images_only',
    label: 'Hotel Images Only',
    description: 'Download only hotel-level images (skip room photos)',
    config: { image_type: 'hotel' },
  },
  {
    name: 'room_images_only',
    label: 'Room Images Only',
    description: 'Download only room-level images',
    config: { image_type: 'room' },
  },
  {
    name: 'small_batch_50k',
    label: 'Small Batch 50K',
    description: '50K hotels — manageable batch size',
    config: { limit: '50000' },
  },
  {
    name: 'medium_batch_200k',
    label: 'Medium Batch 200K',
    description: '200K hotels — ~2-3 hour full pipeline',
    config: { limit: '200000' },
  },
];

const STAGE_OPTIONS: { value: PipelineStageName; label: string }[] = [
  { value: 'scan', label: 'Scan' },
  { value: 'process', label: 'Process (ERS + DB)' },
  { value: 'image_download', label: 'Image Download' },
  { value: 'image_embed', label: 'Image Embed' },
];

export default function NewRunDialog({ open, onClose, onSubmit, loading }: NewRunDialogProps) {
  const [mode, setMode] = useState<PipelineMode>('full');
  const [stage, setStage] = useState<PipelineStageName>('scan');
  const [environment, setEnvironment] = useState<PipelineEnvironment>('staging');
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Advanced config
  const [limit, setLimit] = useState('');
  const [countryAllowlist, setCountryAllowlist] = useState('');
  const [excludeCountries, setExcludeCountries] = useState('');
  const [imageType, setImageType] = useState<'all' | 'hotel' | 'room'>('all');
  const [includePlaceId, setIncludePlaceId] = useState(false);

  const handlePresetClick = (presetName: string) => {
    if (selectedPreset === presetName) {
      setSelectedPreset(null);
      setLimit('');
      setCountryAllowlist('');
      setExcludeCountries('');
      setImageType('all');
      return;
    }
    setSelectedPreset(presetName);
    const preset = BATCH_PRESETS.find((p) => p.name === presetName);
    if (preset) {
      setLimit(preset.config.limit || '');
      setCountryAllowlist(preset.config.country_allowlist || '');
      setExcludeCountries(preset.config.exclude_countries || '');
      setImageType(preset.config.image_type || 'all');
    }
  };

  const handleSubmit = () => {
    const config: PipelineRunConfig = {};
    if (limit) config.limit = limit;
    if (countryAllowlist) config.country_allowlist = countryAllowlist;
    if (excludeCountries) config.exclude_countries = excludeCountries;
    if (imageType !== 'all') config.image_type = imageType;
    if (includePlaceId) config.include_place_id = true;

    const request: CreatePipelineRunRequest = {
      mode,
      environment,
      config,
    };
    if (mode === 'stage') {
      request.stage = stage;
    }
    if (selectedPreset) {
      request.preset = selectedPreset;
    }
    onSubmit(request);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Start Pipeline Run</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {/* Environment */}
          <FormControl fullWidth size="small">
            <InputLabel>Environment</InputLabel>
            <Select
              value={environment}
              label="Environment"
              onChange={(e) => setEnvironment(e.target.value as PipelineEnvironment)}
            >
              <MenuItem value="staging">Staging</MenuItem>
              <MenuItem value="prod">Production</MenuItem>
            </Select>
          </FormControl>

          {environment === 'prod' && (
            <Alert severity="warning" variant="outlined">
              This will run against <strong>production</strong> data. Consider using a preset with a limit.
            </Alert>
          )}

          {/* Mode */}
          <FormControl fullWidth size="small">
            <InputLabel>Mode</InputLabel>
            <Select
              value={mode}
              label="Mode"
              onChange={(e) => setMode(e.target.value as PipelineMode)}
            >
              <MenuItem value="full">Full Pipeline (all stages)</MenuItem>
              <MenuItem value="stage">Single Stage</MenuItem>
            </Select>
          </FormControl>

          {mode === 'stage' && (
            <FormControl fullWidth size="small">
              <InputLabel>Stage</InputLabel>
              <Select
                value={stage}
                label="Stage"
                onChange={(e) => setStage(e.target.value as PipelineStageName)}
              >
                {STAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Batch Presets */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Batch Presets
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {BATCH_PRESETS.map((preset) => (
                <Chip
                  key={preset.name}
                  label={preset.label}
                  onClick={() => handlePresetClick(preset.name)}
                  color={selectedPreset === preset.name ? 'primary' : 'default'}
                  variant={selectedPreset === preset.name ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
            {selectedPreset && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {BATCH_PRESETS.find((p) => p.name === selectedPreset)?.description}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Advanced toggle */}
          <Box
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <Typography variant="subtitle2" color="primary">
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </Typography>
          </Box>

          {showAdvanced && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Hotel Limit"
                  size="small"
                  fullWidth
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder="e.g. 50000"
                  helperText="Max hotels to process"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Image Type</InputLabel>
                  <Select
                    value={imageType}
                    label="Image Type"
                    onChange={(e) => setImageType(e.target.value as 'all' | 'hotel' | 'room')}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="hotel">Hotel Only</MenuItem>
                    <MenuItem value="room">Room Only</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Country Allowlist"
                  size="small"
                  fullWidth
                  value={countryAllowlist}
                  onChange={(e) => setCountryAllowlist(e.target.value)}
                  placeholder="e.g. US,GB,FR"
                  helperText="Comma-separated ISO codes (empty = all)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Exclude Countries"
                  size="small"
                  fullWidth
                  value={excludeCountries}
                  onChange={(e) => setExcludeCountries(e.target.value)}
                  placeholder="e.g. CN,RU"
                  helperText="Comma-separated ISO codes to exclude"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includePlaceId}
                      onChange={(e) => setIncludePlaceId(e.target.checked)}
                    />
                  }
                  label="Include Google Place ID lookup"
                />
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Starting...' : 'Start Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
